import type { LkqListPriceDiscountInfo } from '../types/invoice';
import { LKQ_PRICING_WILDCARD } from './lkqPricingConstants';

function formatDiscountPercent(discountPercentage: number): string {
    const whole = Math.round(discountPercentage * 1000) / 10;
    return Number.isInteger(whole) ? `${whole}%` : `${whole}%`;
}

function formatResolvedKeys(discount: LkqListPriceDiscountInfo): string | null {
    const parts: string[] = [];
    if (discount.insuranceCompanyName) {
        parts.push(`insurance: ${discount.insuranceCompanyName}`);
    } else if (discount.insuranceCompanyId && discount.insuranceCompanyId !== LKQ_PRICING_WILDCARD) {
        parts.push('insurance: (unresolved name)');
    }
    if (discount.supplierName) {
        parts.push(`vendor: ${discount.supplierName}`);
    } else if (discount.supplierId && discount.supplierId !== LKQ_PRICING_WILDCARD) {
        parts.push('vendor: (unresolved name)');
    }
    if (discount.state) {
        const stateLabel = discount.state === LKQ_PRICING_WILDCARD ? 'All' : discount.state;
        parts.push(`state: ${stateLabel} (shop location)`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
}

/** Multi-line tooltip explaining a parts-specific buy-price discount for the invoice detail table. */
export function formatLkqListPriceDiscountTooltip(
    discount: LkqListPriceDiscountInfo | null | undefined,
): string {
    const percentLabel = formatDiscountPercent(discount?.discountPercentage ?? 0);
    const resolvedKeys = discount ? formatResolvedKeys(discount) : null;
    const lines = [
        'List price = extracted invoice rate',
        'Buy price = List price × (1 − discount)',
        'Applies to parts-specific lines, plus Amazon/eBay vendor invoices.',
        '',
        `Discount applied: ${percentLabel}`,
    ];

    if (!discount) {
        lines.push('', 'Discount details are not available for this invoice yet.');
        return lines.join('\n');
    }

    lines.push('Source: Parts-specific and sublet-specific discounts (insurance + vendor + state).');
    if (resolvedKeys) {
        lines.push(`Matched: ${resolvedKeys}`);
    }
    return lines.join('\n');
}
